import React from "react";
import { Link } from "react-router-dom";
import RelatedContentRail from "../components/RelatedContentRail";

const INSIGHT_TRACKS = [
  {
    title: "AI Strategy",
    badge: "Popular",
    description: "From AI roadmap design to governance frameworks for leadership teams.",
    links: [
      { to: "/ai-readiness", label: "AI Readiness Assessment" },
      { to: "/ai", label: "AI Services" },
      { to: "/playbooks", label: "Executive Playbooks" },
    ],
  },
  {
    title: "Technology Trends",
    badge: "Quarterly",
    description: "Actionable trends across AI, cloud, frontend, developer tooling, and automation.",
    links: [
      { to: "/radar", label: "Technology Radar" },
      { to: "/playbooks", label: "Adoption Playbooks" },
      { to: "/outcomes", label: "Client Outcomes" },
    ],
  },
  {
    title: "Growth Systems",
    badge: "Operator",
    description: "Templates and decision systems for product, marketing, and operations teams.",
    links: [
      { to: "/templates", label: "Templates Library" },
      { to: "/academy", label: "Learning Center" },
      { to: "/consulting", label: "Expert Consulting" },
    ],
  },
];

const FEATURED_BRIEFS = [
  {
    title: "From Experiments to Enterprise AI",
    summary: "A practical model for moving from isolated prototypes to production-ready AI programs with ownership, cost governance, and measurable outcomes.",
    cta: "/ai-readiness",
  },
  {
    title: "The 2026 Edge Stack",
    summary: "How modern teams are combining Workers, edge data, and observability to ship globally without platform bloat.",
    cta: "/radar",
  },
  {
    title: "Automation Without Chaos",
    summary: "A framework for scaling process automation while keeping quality control, traceability, and human approval in the loop.",
    cta: "/playbooks",
  },
];

const RELATED_CONTENT = [
  { to: "/ai-readiness", title: "AI Readiness", desc: "Assess capabilities before scaling initiatives." },
  { to: "/playbooks", title: "Execution Playbooks", desc: "Follow proven paths from pilot to production." },
  { to: "/templates", title: "Templates Library", desc: "Use reusable checklists, briefs, and workflows." },
  { to: "/academy", title: "Learning Center", desc: "Build team capability with practical modules." },
];

export default function InsightsHub() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-indigo-900 to-blue-900 text-white px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-indigo-300 font-bold">Insights Hub</p>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 leading-tight max-w-4xl">
            Trends, frameworks, and practical guidance for teams building modern digital businesses.
          </h1>
          <p className="text-slate-300 text-lg mt-5 max-w-3xl">
            Use this hub to discover where technology is heading, what to prioritize now, and how to execute with less guesswork.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/radar" className="px-5 py-2.5 rounded-lg bg-white text-indigo-700 font-bold hover:bg-indigo-50 transition">Explore Radar</Link>
            <Link to="/templates" className="px-5 py-2.5 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition">Open Templates</Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {INSIGHT_TRACKS.map((track) => (
            <div key={track.title} className="border border-gray-200 rounded-2xl p-6 hover:shadow-md transition">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xl font-black text-slate-900">{track.title}</h2>
                <span className="text-[10px] uppercase tracking-wider font-bold bg-indigo-100 text-indigo-700 rounded-full px-2 py-1">{track.badge}</span>
              </div>
              <p className="text-sm text-slate-600 mb-5 leading-relaxed">{track.description}</p>
              <div className="space-y-2">
                {track.links.map((l) => (
                  <Link key={l.to + l.label} to={l.to} className="block text-sm font-semibold text-indigo-700 hover:text-indigo-900">
                    {l.label} →
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-16 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div>
            <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-8">Featured Briefs</h2>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
              {FEATURED_BRIEFS.map((brief) => (
                <div key={brief.title} className="bg-white border border-gray-200 rounded-2xl p-5">
                  <h3 className="font-bold text-slate-900 mb-2">{brief.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">{brief.summary}</p>
                  <Link to={brief.cta} className="text-sm font-semibold text-indigo-700 hover:text-indigo-900">Read now →</Link>
                </div>
              ))}
            </div>
          </div>
          <RelatedContentRail section="insights_hub" links={RELATED_CONTENT} />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-700 text-white p-8 sm:p-10">
          <h2 className="text-3xl font-black mb-3">Build your own insight path</h2>
          <p className="text-indigo-100 max-w-2xl mb-6">
            Start with AI Readiness, continue with Playbooks, and operationalize with Templates. Each page is designed to move you from ideas to execution.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/ai-readiness" className="px-5 py-2.5 rounded-lg bg-white text-indigo-700 font-bold hover:bg-indigo-50 transition">Start Assessment</Link>
            <Link to="/playbooks" className="px-5 py-2.5 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition">Open Playbooks</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
